import React from 'react';
import { useIntl } from 'react-intl';

import { RootState } from 'App/Store';
import { useSelector } from 'hooks';
import useLabelTranslation from 'hooks/useLabelTranslation';

import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { getFormattedFormData, isMobilityAccepted } from 'modules/form/selectors';

import { useGetSelfServiceByIdQuery, useLazyCreatePreviewPDFQuery } from 'modules/selfServices/service';

import { SignDocument } from '../types';
import { getDocuments } from '../utils';

const useDocuments = () => {
  const intl = useIntl();
  const { getLabelTranslation } = useLabelTranslation();

  const selfServiceId = useSelector(getSelectedSelfServiceId);
  const hasAcceptedMobility = useSelector(isMobilityAccepted);

  const { data: selfService } = useGetSelfServiceByIdQuery({ id: selfServiceId });

  const formattedData = useSelector((state: RootState) => getFormattedFormData(state, selfService));

  const [retrieveSummaryDocument, { isLoading }] = useLazyCreatePreviewPDFQuery();

  const [hasError, setHasError] = React.useState(false);
  const [paths, setPaths] = React.useState<Record<string, string>>({});
  const [selectedDocumentIndex, setSelectedDocumentIndex] = React.useState<number>();

  const documents: SignDocument[] = React.useMemo(() => {
    const formatted = getDocuments(selfService, {
      intl, getLabelTranslation, hasAcceptedMobility, retrieveSummaryDocument,
    });

    return formatted.map((doc) => {
      const path = paths[doc.id];
      return { ...doc, path: path ?? doc.path, read: Boolean(path) || doc.read };
    });
  }, [intl, getLabelTranslation, retrieveSummaryDocument, paths, hasAcceptedMobility, selfService]);

  const readDocument = React.useCallback(async (id: string) => {
    const index = documents.findIndex((doc) => doc.id === id);
    setSelectedDocumentIndex(index);
    const document = documents[index];

    try {
      setHasError(false);
      const path = document.path ?? await document.get({ id: selfServiceId, body: formattedData }).unwrap();
      setPaths((oldValue) => ({ ...oldValue, [id]: path }));
    } catch {
      setHasError(true);
    }
  }, [documents, formattedData, selfServiceId]);

  const readNextDocument = React.useCallback(() => {
    const nextIndex = selectedDocumentIndex === documents.length - 1 ? 0 : selectedDocumentIndex + 1;
    const { id: nextDocumentId } = documents[nextIndex];
    readDocument(nextDocumentId);
  }, [documents, readDocument, selectedDocumentIndex]);

  const hasReadAllDocuments = React.useMemo(() => documents.every(({ read }) => read), [documents]);

  return React.useMemo(() => ({
    hasError,
    isLoading,
    documents,
    readDocument,
    readNextDocument,
    hasReadAllDocuments,
    documentIndex: selectedDocumentIndex,
    setDocumentIndex: setSelectedDocumentIndex,
  }), [documents, selectedDocumentIndex, readDocument, hasReadAllDocuments, readNextDocument, isLoading, hasError]);
};

export default useDocuments;
